"use client";

import Image from "next/image";
import Link from "next/link";
import { generateSlug } from "@/lib/utils";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import MemberImage from "@/app/components/Teams/MemberImage";

interface RelatedTherapistsSectionProps {
  serviceSlug: string;
  teamLink: string;
  teamCategory: string;
}

export default function RelatedTherapistsSection({
  serviceSlug,
  teamLink,
  teamCategory,
}: RelatedTherapistsSectionProps) {
  const { teamMembers } = useTeamMembers();

  const teamData = teamMembers.find(
    (team) => team.category === teamCategory
  );
  const therapists = teamData?.members || [];

  // Show up to 6 therapists for the scrolling section
  const displayTherapists = therapists.slice(0, 6);

  if (displayTherapists.length === 0) {
    return null;
  }

  return (
    <section className="py-14 px-4 sm:px-6 bg-gradient-to-b from-white via-gray-50/50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center justify-center mb-4 animate-fade-in group">
            <Image
              src="/verified.png"
              alt="Verified Icon"
              className="w-8 h-8 mr-2 group-hover:rotate-12 transition-transform duration-300"
              width={30}
              height={30}
            />
            <div className="text-md font-light text-white bg-gradient-to-r from-[#54169C] to-[#DA159B] px-6 py-1 rounded-2xl hover:scale-105 hover:shadow-lg hover:shadow-[#DA159B]/30 transition-all duration-300 group-hover:bg-gradient-to-l">
              Our Team
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <h2 className="text-3xl sm:text-4xl font-normal text-[#54169C]">
            Meet Our Therapists
          </h2>
          <Link
            href={teamLink}
            className="hidden md:flex text-[#54169C] hover:text-[#DA159B] font-medium items-center gap-2 transition-colors duration-300 group"
          >
            View All
            <svg
              className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>

        {/* Horizontal Scrolling Container */}
        <div className="relative">
          {/* Gradient fade on the right */}
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-50 via-gray-50/80 to-transparent pointer-events-none z-10 hidden md:block" />
          
          <div className="overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 sm:-mx-6 sm:px-6">
            <div className="flex gap-6 min-w-max">
              {displayTherapists.map((therapist, index) => (
                <Link
                  key={index}
                  href={`/teams/member/${generateSlug(therapist.name)}`}
                  className="flex-shrink-0 w-72 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:-translate-y-2 border border-gray-100"
                >
                  <div className="relative h-64 overflow-hidden bg-gradient-to-br from-[#54169C]/5 to-[#DA159B]/5">
                    <MemberImage
                      name={therapist.name}
                      image={therapist.image}
                      className="object-cover group-hover:scale-110 transition-transform duration-500"
                      sizes="(max-width: 768px) 100vw, 288px"
                      initialsClassName="text-white text-3xl font-bold"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-white text-sm font-medium">
                        {therapist.qualification}
                      </p>
                      <p className="text-white/90 text-xs mt-1">
                        {therapist.experience} Experience
                      </p>
                    </div>
                    {/* Badge overlay */}
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-[#54169C] to-[#DA159B] text-white text-xs font-medium px-3 py-1 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Expert
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-semibold text-gray-800 mb-1 group-hover:text-[#54169C] transition-colors duration-300">
                      {therapist.name}
                    </h3>
                    <p className="text-sm text-[#DA159B] font-medium mb-2">
                      {therapist.role}
                      {therapist.subRole && (
                        <span className="text-gray-600 block text-xs mt-1">
                          {therapist.subRole}
                        </span>
                      )}
                    </p>
                    {therapist.certifications.length > 0 && (
                      <p className="text-xs text-gray-500 line-clamp-2">
                        {therapist.certifications[0]}
                        {therapist.certifications.length > 1 && (
                          <span className="text-[#54169C]"> + {therapist.certifications.length - 1} more</span>
                        )}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* View All Link (Mobile) */}
        <div className="flex justify-center mt-8 md:hidden">
          <Link
            href={teamLink}
            className="px-8 py-3 bg-gradient-to-r from-[#54169C] to-[#DA159B] text-white rounded-full font-medium hover:shadow-lg hover:shadow-[#DA159B]/30 transition-all duration-300 hover:scale-105"
          >
            View All Therapists
          </Link>
        </div>
      </div>
    </section>
  );
}
